import React, { useMemo } from "react";
import {
  Check, GitBranch, Layers, Shield, User, Users, X,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
  PANEL_MENU_PERMISSION_GROUPS,
  USER_CORE_PERMISSION_BADGES,
  getEffectivePanelMenuPermissions,
  isPanelMenuPermissionsConfigured,
} from "@/utils/panelMenuPermissions";
import { panelRoleLabel } from "@/utils/panelRoles";

const parseIdCsv = (csv) => {
  if (!csv) return [];
  if (Array.isArray(csv)) return csv.map(Number).filter((n) => !Number.isNaN(n) && n > 0);
  return String(csv).split(",").map((id) => parseInt(id.trim(), 10)).filter((id) => !Number.isNaN(id) && id > 0);
};

const gorevLabelMap = {
  call_center_yonetici: "Call Center Yönetici",
  takim_lideri: "Takım Lideri",
  dataci: "Datacı",
  kalite: "Kalite Ekibi",
  yonetici: "Yönetici",
  asistan: "Asistan",
};

const AccessRow = ({ allowed, label, note, isDark }) => (
  <div className={`flex items-start gap-2.5 rounded-lg px-2.5 py-2 ${isDark ? "hover:bg-slate-800/50" : "hover:bg-gray-50"}`}>
    <span className={`mt-0.5 shrink-0 inline-flex h-5 w-5 items-center justify-center rounded-full ${allowed
      ? (isDark ? "bg-emerald-500/20 text-emerald-400" : "bg-emerald-100 text-emerald-700")
      : (isDark ? "bg-slate-800 text-slate-500" : "bg-gray-100 text-gray-400")
    }`}>
      {allowed ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
    </span>
    <span className="min-w-0">
      <span className={`block text-sm ${allowed
        ? (isDark ? "text-slate-200" : "text-gray-800")
        : (isDark ? "text-slate-500" : "text-gray-400")
      }`}>{label}</span>
      {note ? <span className={`block text-[11px] mt-0.5 ${isDark ? "text-slate-500" : "text-gray-500"}`}>{note}</span> : null}
    </span>
  </div>
);

const ScopeList = ({ title, items, emptyText, isDark, labelClass }) => (
  <div>
    <p className={`text-xs font-semibold mb-1.5 ${isDark ? "text-slate-300" : "text-gray-700"}`}>{title}</p>
    {items.length === 0 ? (
      <p className={`text-xs italic ${labelClass}`}>{emptyText}</p>
    ) : (
      <div className="flex flex-wrap gap-1.5">
        {items.map((item) => (
          <Badge
            key={item.key}
            variant="outline"
            className={`text-[11px] font-normal ${isDark ? "border-slate-600 text-slate-300" : ""}`}
          >
            {item.label}
          </Badge>
        ))}
      </div>
    )}
  </div>
);

const UserAccessDetailModal = ({
  open,
  onClose,
  user,
  isDark,
  textClass,
  labelClass,
  panelRoles = [],
  teams = [],
  agents = [],
  panelUsers = [],
  gorevLabelFn,
}) => {
  const isAdminUser = user?.is_admin === 1 || user?.is_super_admin === 1;

  const menuPermissions = useMemo(() => {
    if (!user || isAdminUser) return null;
    if (!isPanelMenuPermissionsConfigured(user)) return null;
    return getEffectivePanelMenuPermissions(user);
  }, [user, isAdminUser]);

  const legacyFullMenu = !isAdminUser && user && !isPanelMenuPermissionsConfigured(user);

  const scopeAgents = useMemo(() => {
    if (!user || isAdminUser) return { ids: new Set(), list: [], unlimited: isAdminUser };

    if (user.firma_yonetimi === 1 && user.company_id) {
      const list = agents.filter((a) => Number(a.company_id) === Number(user.company_id));
      return { ids: new Set(list.map((a) => Number(a.id))), list, unlimited: false, companyWide: true };
    }

    const ids = new Set();
    const gorev = user.gorev || "";

    const addTeamAgents = (team) => {
      if (!team) return;
      const teamAgentIds = team.agent_ids
        || (Array.isArray(team.agents) ? team.agents.map((a) => a.id) : []);
      teamAgentIds.forEach((id) => ids.add(Number(id)));
    };

    if (gorev === "call_center_yonetici") {
      parseIdCsv(user.gorebilir_takim_liderleri).forEach((leaderId) => {
        teams.filter((t) => Number(t.lider_user_id) === Number(leaderId)).forEach(addTeamAgents);
      });
    }

    if (gorev === "takim_lideri") {
      teams.filter((t) => Number(t.lider_user_id) === Number(user.id)).forEach(addTeamAgents);
    }

    parseIdCsv(user.gorebilir_takimlar).forEach((teamId) => {
      addTeamAgents(teams.find((t) => Number(t.id) === Number(teamId)));
    });
    parseIdCsv(user.gorebilir_agentlar).forEach((id) => ids.add(Number(id)));

    if (gorev === "asistan" && user.linked_agent_id) {
      ids.clear();
      ids.add(Number(user.linked_agent_id));
    }

    if (!gorev) {
      const legacy = parseIdCsv(user.gorebilecegi_agentlar);
      if (legacy.length === 0) {
        return { ids: new Set(), list: [], unlimited: true };
      }
      legacy.forEach((id) => ids.add(Number(id)));
    }

    const list = agents.filter((a) => ids.has(Number(a.id)));
    return { ids, list, unlimited: false };
  }, [user, isAdminUser, teams, agents]);

  const teamLeaderItems = useMemo(() => {
    if (!user) return [];
    return parseIdCsv(user.gorebilir_takim_liderleri).map((id) => {
      const leader = panelUsers.find((u) => Number(u.id) === Number(id));
      return { key: id, label: leader ? `${leader.ad_soyad} (@${leader.kullanici_adi})` : `Kullanıcı #${id}` };
    });
  }, [user, panelUsers]);

  const teamItems = useMemo(() => {
    if (!user) return [];
    return parseIdCsv(user.gorebilir_takimlar).map((id) => {
      const team = teams.find((t) => Number(t.id) === Number(id));
      return { key: id, label: team ? `${team.ad} (${team.agent_count || 0} agent)` : `Takım #${id}` };
    });
  }, [user, teams]);

  const extraAgentItems = useMemo(() => {
    if (!user) return [];
    return parseIdCsv(user.gorebilir_agentlar).map((id) => {
      const agent = agents.find((a) => Number(a.id) === Number(id));
      return { key: id, label: agent?.ad_soyad || `Agent #${id}` };
    });
  }, [user, agents]);

  const companyTeamLeaderItems = useMemo(() => {
    if (!user?.company_id || user.firma_yonetimi !== 1) return [];
    const leaderIds = new Set(
      teams
        .filter((t) => Number(t.company_id) === Number(user.company_id) && t.lider_user_id)
        .map((t) => Number(t.lider_user_id))
    );
    return [...leaderIds].map((id) => {
      const leader = panelUsers.find((u) => Number(u.id) === Number(id));
      return { key: id, label: leader ? `${leader.ad_soyad} (@${leader.kullanici_adi})` : `Kullanıcı #${id}` };
    });
  }, [user, teams, panelUsers]);

  const legacyAgentItems = useMemo(() => {
    if (!user?.gorev) {
      return parseIdCsv(user?.gorebilecegi_agentlar).map((id) => {
        const agent = agents.find((a) => Number(a.id) === Number(id));
        return { key: id, label: agent?.ad_soyad || `Agent #${id}` };
      });
    }
    return [];
  }, [user, agents]);

  if (!user) return null;

  const blockClass = isDark ? "border-slate-800 bg-slate-950/40" : "border-gray-200 bg-gray-50/80";
  const gorevText = user.gorev
    ? (gorevLabelFn ? gorevLabelFn(user.gorev) : gorevLabelMap[user.gorev] || user.gorev)
    : "Eski sistem (manuel agent listesi)";

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className={`max-w-2xl max-h-[90vh] overflow-hidden flex flex-col p-0 gap-0 ${isDark ? "bg-slate-900 border-slate-700 text-white" : ""}`}>
        <DialogHeader className={`shrink-0 px-6 pt-6 pb-4 border-b ${isDark ? "border-slate-800" : "border-gray-100"}`}>
          <DialogTitle className="flex items-start gap-3 pr-6">
            <span className={`inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${isDark ? "bg-indigo-500/20 text-indigo-300" : "bg-indigo-100 text-indigo-700"}`}>
              <Shield className="w-5 h-5" />
            </span>
            <span className="min-w-0 text-left">
              <span className={`block text-lg font-semibold truncate ${textClass}`}>{user.ad_soyad || user.kullanici_adi}</span>
              <span className={`block text-sm font-normal mt-0.5 ${labelClass}`}>@{user.kullanici_adi}</span>
            </span>
          </DialogTitle>
          <div className="flex flex-wrap gap-2 mt-3">
            {user.is_super_admin === 1 && (
              <Badge className={isDark ? "bg-amber-500/15 text-amber-300 border-amber-500/30" : "bg-amber-50 text-amber-700 border-amber-200"}>
                Süper Admin
              </Badge>
            )}
            {user.is_admin === 1 && user.is_super_admin !== 1 && (
              <Badge className={isDark ? "bg-purple-500/15 text-purple-300 border-purple-500/30" : "bg-purple-50 text-purple-700 border-purple-200"}>
                Admin
              </Badge>
            )}
            {!isAdminUser && (
              <>
                <Badge variant="outline" className={isDark ? "border-indigo-500/30 text-indigo-300" : "border-indigo-200 text-indigo-700"}>
                  {gorevText}
                </Badge>
                {user.panel_role_id ? (
                  <Badge variant="outline" className={isDark ? "border-cyan-500/30 text-cyan-300" : "border-cyan-200 text-cyan-700"}>
                    {panelRoleLabel(panelRoles, user.panel_role_id)}
                  </Badge>
                ) : null}
              </>
            )}
            {user.company_name || user.firma_adi ? (
              <Badge variant="secondary" className={isDark ? "bg-slate-800 text-slate-300" : ""}>
                {user.company_name || user.firma_adi}
              </Badge>
            ) : null}
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {isAdminUser ? (
            <div className={`rounded-xl border p-4 ${isDark ? "border-purple-500/25 bg-purple-950/20" : "border-purple-200 bg-purple-50/80"}`}>
              <p className={`text-sm font-semibold ${isDark ? "text-purple-200" : "text-purple-800"}`}>Tam panel erişimi</p>
              <p className={`text-xs mt-1 ${labelClass}`}>Admin kullanıcılar tüm sayfaları, tüm agentları ve tüm ana yetkileri kullanır.</p>
            </div>
          ) : (
            <>
              <section className={`rounded-xl border p-4 ${blockClass}`}>
                <p className={`text-sm font-semibold flex items-center gap-2 mb-3 ${textClass}`}>
                  <Shield className="w-4 h-4 text-violet-500" /> Ana Yetkiler
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-0.5">
                  {USER_CORE_PERMISSION_BADGES.map(({ key, title }) => (
                    <AccessRow
                      key={key}
                      allowed={user[key] === 1}
                      label={title}
                      isDark={isDark}
                    />
                  ))}
                </div>
              </section>

              <section className={`rounded-xl border p-4 ${blockClass}`}>
                <p className={`text-sm font-semibold flex items-center gap-2 mb-3 ${textClass}`}>
                  <Layers className="w-4 h-4 text-cyan-500" /> Sayfa Erişimleri
                </p>
                {legacyFullMenu ? (
                  <p className={`text-sm ${isDark ? "text-emerald-300" : "text-emerald-700"}`}>
                    Özel kısıt tanımlı değil — varsayılan olarak tüm menü sayfalarına erişir.
                  </p>
                ) : (
                  <div className="space-y-4">
                    {PANEL_MENU_PERMISSION_GROUPS.map((group) => (
                      <div key={group.id}>
                        <p className={`text-[11px] font-semibold uppercase tracking-wide mb-1.5 ${labelClass}`}>{group.label}</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-0.5">
                          {group.items.map(({ key, label, note }) => (
                            <AccessRow
                              key={key}
                              allowed={menuPermissions?.[key] === 1}
                              label={label}
                              note={note}
                              isDark={isDark}
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              <section className={`rounded-xl border p-4 ${blockClass}`}>
                <p className={`text-sm font-semibold flex items-center gap-2 mb-3 ${textClass}`}>
                  <Users className="w-4 h-4 text-indigo-500" /> Agent Görünürlük Kapsamı
                </p>
                <div className="space-y-4">
                  {user.firma_yonetimi === 1 && user.company_id && (
                    <>
                      <p className={`text-xs rounded-lg px-3 py-2 ${isDark ? "bg-cyan-500/10 text-cyan-300 border border-cyan-500/20" : "bg-cyan-50 text-cyan-800 border border-cyan-100"}`}>
                        Firma yönetimi aktif — bağlı olduğu firmadaki tüm agentlar, takım liderleri ve veriler görünür. Firma ayarları (CRM, transkript, webhook) yönetilebilir.
                      </p>
                      <ScopeList
                        title="Firmadaki takım liderleri"
                        items={companyTeamLeaderItems}
                        emptyText="Bu firmada tanımlı takım lideri yok"
                        isDark={isDark}
                        labelClass={labelClass}
                      />
                    </>
                  )}
                  {user.gorev === "call_center_yonetici" && (
                    <ScopeList
                      title="Görülecek takım liderleri"
                      items={teamLeaderItems}
                      emptyText="Takım lideri seçilmemiş"
                      isDark={isDark}
                      labelClass={labelClass}
                    />
                  )}
                  {user.gorev === "takim_lideri" && (
                    <p className={`text-xs rounded-lg px-3 py-2 ${isDark ? "bg-indigo-500/10 text-indigo-300 border border-indigo-500/20" : "bg-indigo-50 text-indigo-800 border border-indigo-100"}`}>
                      Lideri olduğu takımlar otomatik dahil edilir.
                    </p>
                  )}
                  {user.gorev === "asistan" && user.linked_agent_id && (
                    <ScopeList
                      title="Bağlı agent"
                      items={[{
                        key: user.linked_agent_id,
                        label: agents.find((a) => Number(a.id) === Number(user.linked_agent_id))?.ad_soyad || `Agent #${user.linked_agent_id}`,
                      }]}
                      emptyText="Bağlı agent yok"
                      isDark={isDark}
                      labelClass={labelClass}
                    />
                  )}
                  {!user.gorev && legacyAgentItems.length === 0 && (
                    <p className={`text-xs ${isDark ? "text-emerald-300" : "text-emerald-700"}`}>Manuel liste boş — tüm agentları görür (eski sistem).</p>
                  )}
                  {!user.gorev && legacyAgentItems.length > 0 && (
                    <ScopeList
                      title="Manuel seçili agentlar"
                      items={legacyAgentItems}
                      emptyText="—"
                      isDark={isDark}
                      labelClass={labelClass}
                    />
                  )}
                  {["call_center_yonetici", "takim_lideri", "dataci", "kalite", "yonetici"].includes(user.gorev) && (
                    <>
                      <ScopeList
                        title="Ek takımlar"
                        items={teamItems}
                        emptyText="Ek takım seçilmemiş"
                        isDark={isDark}
                        labelClass={labelClass}
                      />
                      <ScopeList
                        title="Ek tekil agentlar"
                        items={extraAgentItems}
                        emptyText="Ek agent seçilmemiş"
                        isDark={isDark}
                        labelClass={labelClass}
                      />
                    </>
                  )}
                  <div className={`rounded-lg border px-3 py-2.5 ${isDark ? "border-emerald-500/25 bg-emerald-950/20" : "border-emerald-200 bg-emerald-50/80"}`}>
                    <p className={`text-xs font-semibold ${isDark ? "text-emerald-300" : "text-emerald-800"}`}>
                      {scopeAgents.companyWide
                        ? `Görünür agentlar: Firmanın tümü (${scopeAgents.list.length} kişi)`
                        : scopeAgents.unlimited
                        ? "Görünür agentlar: Tümü"
                        : `Görünür agentlar: ${scopeAgents.list.length} kişi`}
                    </p>
                    {!scopeAgents.unlimited && scopeAgents.list.length > 0 && (
                      <p className={`text-xs mt-1.5 leading-relaxed ${isDark ? "text-slate-300" : "text-gray-700"}`}>
                        {scopeAgents.list.slice(0, 20).map((a) => a.ad_soyad).join(", ")}
                        {scopeAgents.list.length > 20 ? ` … +${scopeAgents.list.length - 20} daha` : ""}
                      </p>
                    )}
                    {!scopeAgents.unlimited && scopeAgents.list.length === 0 && (
                      <p className={`text-xs mt-1 ${isDark ? "text-amber-300" : "text-amber-700"}`}>Henüz kapsamda agent yok</p>
                    )}
                  </div>
                </div>
              </section>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UserAccessDetailModal;
